import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FileText, Trash2, Clock } from "lucide-react";
import { api, money } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { toast } from "sonner";

export default function QuotationsList() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setQuotes(await api("/quotations")); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this quotation?")) return;
    try { await api(`/quotations/${id}`, { method: "DELETE" }); toast.success("Deleted"); load(); } catch (e2) { toast.error(e2.message); }
  };

  return (
    <div>
      <PageHeader title="Quotations" subtitle={`${quotes.length} total`}
        actions={<Link to="/quotation-new" data-testid="new-quote-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"><Plus className="h-4 w-4" />New Quotation</Link>} />
      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> : quotes.length === 0 ? (
        <EmptyState icon={FileText} title="No quotations yet" description="Create a quote with VAT-inclusive totals to send to a customer."
          action={<Link to="/quotation-new" className="rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white">New Quotation</Link>} />
      ) : (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {quotes.map((q) => (
            <div key={q.id} data-testid={`quote-row-${q.id}`} onClick={() => navigate(`/quotations/${q.id}`)} className="flex cursor-pointer items-center gap-4 px-4 py-4 hover:bg-secondary">
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-midnight">{q.title}</p><p className="text-xs text-muted-foreground">{q.quotation_number ? `${q.quotation_number} · ` : ""}Valid until {fmtDate(q.valid_until)}</p></div>
              <p className="font-display text-lg font-bold text-midnight">{money(q.totals?.gross)}</p>
              {q.status === "sent" && (
                <span data-testid={`quote-awaiting-approval-${q.id}`} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">
                  <Clock className="h-3 w-3" />Awaiting approval
                </span>
              )}
              <StatusChip status={q.status === "accepted" ? "completed" : q.status === "rejected" ? "unpaid" : "open"} />
              <button onClick={(e) => remove(e, q.id)} className="text-danger"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
